/* eslint-disable no-console */
import { useCallback, useState } from 'react';
import { CareerId } from '@common/const/catalog/career';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { logError, resetLoading, toError } from '../lib/result';
import { OrderNannyParams, useOrderNannyModals } from './orderNanny';
import { useNavigateToDialog } from './useNavigateToDialog';

const namespace = 'OrderNanny';

type OrderNannyFeature = (catalogItemId: number, params?: OrderNannyParams) => AsyncResult<void, Error>;

export const useOrderNanny = (): [OrderNannyFeature, boolean] => {
  const [isLoading, setIsLoading] = useState(false);
  const openNannyModals = useOrderNannyModals();
  const navigateToDialog = useNavigateToDialog(namespace);
  const [connectWithAuthor] = coreCatalogItemApi.endpoints.connectWithAuthor.useMutation();
  const [queryCatalogItem] = coreCatalogItemApi.endpoints.getById.useLazyQuery();

  const processRunner = useCallback<OrderNannyFeature>(
    async (catalogItemId, params) => {
      console.debug(`[OrderNanny]: Starting the proccess with ${catalogItemId}...`);
      setIsLoading(true);

      const catalogItemResult = await Result.fromAsync(() => queryCatalogItem({ id: catalogItemId }).unwrap(), toError);

      const connectionResult = await catalogItemResult.mapAsync(async (catalogItem) => {
        let dialogId = params?.dialogId;
        if (!dialogId) {
          const connectResponse = await connectWithAuthor({ id: catalogItem.id }).unwrap();
          dialogId = connectResponse.dialogId;
        }
        return { catalogItem, dialogId };
      });

      const orderNannyResult = await connectionResult.mapAsync(async ({ catalogItem, dialogId }) => {
        if (catalogItem.careerId === +CareerId.Nanny) {
          return openNannyModals(catalogItem, dialogId, params?.initiatorId);
        }
        return navigateToDialog(dialogId);
      });

      return orderNannyResult
        .tap(() => console.debug(`[${namespace}]: The process was completed successfully!`))
        .tap(resetLoading(setIsLoading))
        .tapError(resetLoading(setIsLoading))
        .tapError(logError(namespace));
    },
    [queryCatalogItem, connectWithAuthor, navigateToDialog, openNannyModals],
  );

  return [processRunner, isLoading];
};
